// Declared in SharedModule next to LocationPickerComponent, the Google picker it stands in for.
/* eslint-disable @angular-eslint/prefer-standalone */
import { CropBox, SheetPreviewCrop } from '../../helpers/sheet-preview-crop';
import { SheetPreviewService } from '../../services/sheet-preview.service';
import { markupBBox } from '../../../modules/projects/pages/projects-details-page/components/tab-screens/drawings/drawing-as-built';
import { confirmLinkRemoval, PINLESS_EMPTY_CONFIRM } from '../../helpers/link-removal';
import { ChangeDetectorRef, Component, OnDestroy, ViewChild, inject } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Ability, subject } from '@casl/ability';
import { Subscription, firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import Graphic from '@arcgis/core/Graphic';
import Extent from '@arcgis/core/geometry/Extent';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Point from '@arcgis/core/geometry/Point';
import PictureMarkerSymbol from '@arcgis/core/symbols/PictureMarkerSymbol';
import { MapReferenceLayerService } from '../../services/map-reference-layer.service';
import { ProjectMapInspectionService } from '../../services/project-map-inspection.service';
import { DesignContextAvailabilityService } from '../../services/design-context-availability.service';
import { AuthService } from '../../services/auth.service';
import { DailyReportService } from '../../../modules/projects/pages/site-details-page/tab-screens/daily-report/daily-report.service';
import { MapCaptureService } from '../../services/map-capture.service';
import { clampZoomToPublishedMax } from '../../services/project-map-manifest.service';
import { EsriMapComponent } from '../esri-map/esri-map.component';
import { DEFAULT_MAP_TOOLBAR_STATE, MapToolbarState } from '../../types/map.types';
import { loadBasemapPreference, resolveBasemap, saveBasemapPreference } from '../../helpers/map-layer-view-state';
import { currentLocation, projectDetails } from '../../../modules/projects/core/projects.selectors';
import { FRAME_USER_RADIUS_M, pickerFrame, withinBounds } from '../../helpers/picker-framing';
import { MarkupService } from '../../services/markup.service';
import { Markup } from '../../models/markup';
import { MapFeatureLink, MapFeatureLinkCapture } from '../../models/map-feature-link';
import { DrawingRevisionDetail } from '../../models/drawing-sheet';
import {
  captureAfterMarkupSave, captureNeedsRetake, designContextStripLines, draftMapFeatureLink, draftSheetLink, linkCaptureImage, withCapture, heldStrokesFor,
  savedMarkupKind,
} from '../../helpers/design-context';
import { ProjectDrawingsService } from '../../services/project-drawings.service';
import { HeldSheetMarkup, heldMarkup, newClientKey, SheetMarkupQueueService } from '../../services/sheet-markup-queue.service';
import { DesignContextSheetPickerComponent, SheetMarkUpTarget, SheetPointPick } from '../design-context-sheet-picker/design-context-sheet-picker.component';
import { MapViewCapture, captureMapView } from '../../helpers/design-context-capture';
import { CAPTURE_FAILED, CAPTURE_UPLOAD_FAILED, CaptureSession } from '../../helpers/design-context-capture-session';
import { MarkupEditorConfig, MarkupSaveResult } from '../markup-editor/markup-editor.types';
import { DrawingsAnalytics } from '../../../modules/projects/pages/projects-details-page/components/tab-screens/drawings/drawings-analytics';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../confirm-dialog/confirm-dialog.component';
import {
  buildPickQuery, drawingDoneBlocked, PICKER_SAVE_LABEL, PickerChanges, PickerTab, pickResult, PickResult, sheetPinRemoved, sheetTileCrop,
  sheetTileLabel,
} from '../../helpers/design-context-pick';
import { featureMatchesWhere, featureSourceOnlyLayer } from '../../helpers/feature-source-prefill';
import { expandLauncherProperties, launcherCandidateLabel, matchLauncherRoute, ResolvedLauncherRoute } from '../../helpers/feature-launcher';
import {
  doneCaption, entryInReport, FeatureLinkEntry, launcherPointStatus, launcherRowEntry, shortReportDate,
} from '../../helpers/feature-link-status';
import { ProjectMapManifestService } from '../../services/project-map-manifest.service';
import type { FeatureSource } from '../../models/control-form';
import type { PublishedMapLayer } from '../../services/project-map-manifest.service';
import {
  distanceText, featureHighlightSymbol, featureLabel, geoJsonToArcGis, InspectionQuery, layerDrawsAtScale, MapFeatureCandidate, titleLookAlikes,
} from '../../helpers/project-map-inspection';

export interface DesignContextPickerData {
  projectId: string;
  latitude?: number | string | null;
  longitude?: number | string | null;
  location?: string | null;
  locationText?: string | null;
  enableLocationSection?: boolean;
  showIncludeGpsLocationCheckbox?: boolean;
  /** The record's current link. Passing it makes the picker own the link (Done always sends the key). */
  existingLink?: MapFeatureLink | null;
  /** "issue", "bid item", "activity", … for the "Link to this <record>" button. */
  recordLabel?: string;
  /** 7.2: the record's markup starts as this kind (As-built for an issue in an as-built category); default note */
  markupKind?: 'note' | 'as_built' | null;
  /** The record's station in feet, when known: the Drawing tab lists sheets covering it first. */
  station?: number | null;
  /** False when the launcher opened the picker for the project's drawings only (no published map catalog). */
  hasCatalog?: boolean;
  /**
   * Whether the project has published sheets; the Drawing tab appears once this resolves true (it may land after the
   * picker opened). Omitted: the tab follows the drawing read permission alone.
   */
  drawingsAvailable?: Promise<boolean>;
  /**
   * W49: the tab to open on. Map by default; 'drawing' when the record's ＋ menu "Mark on drawing" opened the picker
   * (the Drawing tab once it is available).
   */
  initialTab?: 'map' | 'drawing';
  /**
   * The form's feature_source: `picker: "only"` shows and inspects that layer alone (a session-only layer state,
   * never saved to the user's preferences), `where` filters its candidates, and Done returns the linked feature's
   * properties as `feature_properties`.
   */
  featureSource?: FeatureSource | null;
  /**
   * A launcher form's map-first pick (ui_config.feature_launcher): only `layer` is shown and picked (all its features),
   * only features matching a route are offered (rows: label_fields + target form name), and linking one closes the
   * picker with the link and the feature's properties; the host opens the routed form.
   */
  featureLauncher?: PickerFeatureLauncher | null;
}

/** `feature_launcher` with its routes resolved to the project's forms (see resolveLauncherRoutes). */
export interface PickerFeatureLauncher {
  layer: string;
  title?: string;
  label_fields?: string[];
  routes: ResolvedLauncherRoute[];
  /** Property -> separator: one row per value of a routed multi-value property (same point, same link). */
  multi_value_fields?: Record<string, string>;
  /** The daily report the launcher was opened from: a done row's entry in it can be opened directly. */
  currentReport?: { id?: unknown; uuid?: unknown } | null;
}

type SessionLayerState = Partial<{ visible: boolean; labels: boolean }>;

const MAX_PICK_CHOICES = 6;
/** Same fallback centre as the Google picker when the record has no pin yet. */
const DEFAULT_PIN = { latitude: 30.26798, longitude: -97.74083 };
/** Built per graphic: `view.destroy()` cascades to layers, graphics and their symbols, so none may be shared across dialogs. */
const pinSymbol = () => new PictureMarkerSymbol({ url: '/assets/icons/map/markers/location-pin.svg', width: 20, height: 27, yoffset: 13 });
const LAYERS_UNAVAILABLE = 'Design layers are unavailable right now. You can still place the pin.';
const NO_CATALOG = 'This project has no published design layers. You can still place the pin.';
const SHEET_UNAVAILABLE = 'This sheet could not be opened. Try again.';
/** Framing waits this long for the MapView to be ready; past it the picker keeps its starting camera. */
const VIEW_READY_TIMEOUT_MS = 5000;
const LAUNCHER_LAYER_UNAVAILABLE = "This form's map layer isn't available.";
const ACCESS_CHANGED = 'Design layer access has changed. Close and reopen the picker.';

/**
 * The Location pin on the ArcGIS map (spec §7.1): drop or drag a pin, see the
 * published design layers, tap a feature to link it. Opened by
 * `LocationPickerLauncherService` only for projects with a published catalog.
 */
@Component({
  selector: 'app-design-context-picker-dialog',
  templateUrl: './design-context-picker-dialog.component.html',
  styleUrls: ['./design-context-picker-dialog.component.scss'],
  providers: [MapReferenceLayerService],
  standalone: false,
})
export class DesignContextPickerDialogComponent implements OnDestroy {
  dialogRef = inject<MatDialogRef<DesignContextPickerDialogComponent, PickResult>>(MatDialogRef);
  data = inject<DesignContextPickerData>(MAT_DIALOG_DATA);
  private referenceLayers = inject(MapReferenceLayerService);
  /** W51: PostHog (target and whether marked up; never the place) */
  private track = inject(DrawingsAnalytics);
  private inspection = inject(ProjectMapInspectionService);
  private availability = inject(DesignContextAvailabilityService);
  private auth = inject(AuthService);
  private dailyReportService = inject(DailyReportService);
  private cdr = inject(ChangeDetectorRef);
  private captureService = inject(MapCaptureService);
  private markupService = inject(MarkupService);
  private dialog = inject(MatDialog);
  private store = inject(Store);
  private drawings = inject(ProjectDrawingsService);
  private sheetMarkups = inject(SheetMarkupQueueService);
  private ability = inject(Ability);
  private manifestApi = inject(ProjectMapManifestService);
  /** Launcher: records linked to each feature of its layer (done vs pending); null until loaded or when it failed. */
  linkSummary: Record<string, FeatureLinkEntry[]> | null = null;

  latitude: number;
  longitude: number;
  /** Set once for the dialog's lifetime; `app-esri-map` recentres on every change of this array. */
  readonly center: number[];
  includeGps = true;
  /** Include GPS as the dialog opened (a toggle away from it is a change) */
  private readonly openedWithGps = true;
  locationForm = new FormGroup({ location: new FormControl('') });
  link: MapFeatureLink | null;
  layersMessage = '';
  loading = false;
  message = '';
  incomplete = false;
  candidates: MapFeatureCandidate[] = [];
  hidden = 0;
  selected: MapFeatureCandidate | null = null;
  view: 'list' | 'details' = 'list';
  /** Mark up (spec 2026-09-27 §2): the capture is uploaded the moment it is taken; the editor works on the upload. */
  capturing = false;
  captureError = '';
  showMarkupEditor = false;
  markupConfig: MarkupEditorConfig | null = null;
  private readonly session = new CaptureSession();
  /** The main map's layers + basemap control, contained in the picker map (Sri 2026-09-28). */
  @ViewChild(EsriMapComponent) private esriMap?: EsriMapComponent;
  @ViewChild(DesignContextSheetPickerComponent) private sheetPicker?: DesignContextSheetPickerComponent;
  layersToolbar: MapToolbarState = { ...DEFAULT_MAP_TOOLBAR_STATE };
  organizationBasemaps: { id?: string }[] = [];
  /** The per-user, per-project basemap, shared with the project map (map-layer-view-state). */
  basemapId: string | null = null;
  private layersChangedSub?: Subscription;
  /** Map or Drawing (spec §12 slice 3): a record linked to a sheet only opens on its sheet. */
  tab: PickerTab = 'map';
  /** The Drawing tab exists for users who can read the project's drawings, once the project is known to have sheets. */
  canUseDrawings = false;
  /** The markup editor is open on the map capture or on the linked sheet page. */
  private editorTarget: 'capture' | 'sheet' = 'capture';
  /** The revision behind the link's sheet target (its set file is what the editor opens). */
  private sheetRevision: DrawingRevisionDetail | null = null;
  /** Strokes drawn this session on a sheet point whose link is not saved yet; posted after the record saves. */
  private heldSheet: HeldSheetMarkup | null = null;
  /** Mark up on a sheet the link is not on yet (no pin): the link follows only once strokes are saved */
  private pendingSheet: SheetMarkUpTarget | null = null;

  private readonly existingLink: MapFeatureLink | null;
  /** feature_source `picker: "only"` layer id, when configured. */
  private readonly onlyLayer: string | null;
  /**
   * Session layer visibility/labels while feature_source restricts the picker (null otherwise). Layer changes made
   * here then stay in this dialog: the user's saved layer preferences are not touched.
   */
  private sessionLayers: Record<string, SessionLayerState> | null = null;
  /** Properties of the feature linked in this session, returned for the feature_source prefill. */
  private linkedProperties: Record<string, unknown> | null = null;
  /** feature_source `where` drawing: the last view had no matching features (shows `whereEmptyMessage`). */
  noMatchingFeatures = false;
  private linkTouched = false;
  private pinMoved = false;
  private mapView: __esri.MapView | null = null;
  private pinLayer: GraphicsLayer | null = null;
  private highlightLayer: GraphicsLayer | null = null;
  private pinGraphic: Graphic | null = null;
  private handles: { remove(): void }[] = [];
  private controller?: AbortController;
  private generation = 0;
  private query?: InspectionQuery;
  private dragging = false;
  private destroyed = false;
  /** W75: the linked page's width / height, for the src it was measured on */
  private sheetTileAspect: { src: string; aspect: number } | null = null;
  /** Audit 3: the saved row's strokes box for the linked revision (fetched on open) */
  private savedStrokes: { revision: string; box: CropBox } | null = null;
  private readonly sheetPreviews = inject(SheetPreviewService);

  constructor() {
    const data = this.data;
    this.existingLink = data.existingLink || null;
    this.onlyLayer = data.featureLauncher?.layer || featureSourceOnlyLayer(data.featureSource);
    this.link = this.existingLink;
    const latitude = Number(data.latitude);
    const longitude = Number(data.longitude);
    const seed = hasCoordinates(data.latitude, data.longitude) && Number.isFinite(latitude) && Number.isFinite(longitude)
      ? { latitude, longitude }
      : this.existingLink?.pin ? { latitude: Number(this.existingLink.pin.latitude), longitude: Number(this.existingLink.pin.longitude) } : DEFAULT_PIN;
    const canReadDrawings = !!data.projectId && this.ability.can('read', subject('ProjectDrawing', { project_id: data.projectId }));
    if (canReadDrawings) {
      if (data.drawingsAvailable) {
        data.drawingsAvailable.then(found => {
          if (found && !this.destroyed) this.enableDrawings(!this.linkTouched && !this.pinMoved);
        }).catch(() => undefined);
      } else {
        this.enableDrawings(true, false);
      }
    }
    this.latitude = seed.latitude;
    this.longitude = seed.longitude;
    this.center = [this.longitude, this.latitude];
    if (data.enableLocationSection) this.locationForm.controls.location.setValue(data.locationText || data.location || '');
    this.store.select(projectDetails).pipe(take(1)).subscribe(project => {
      const basemaps = (project as { id?: string; base_maps?: { id?: string }[] } | null);
      if (basemaps?.id === data.projectId && Array.isArray(basemaps.base_maps)) this.organizationBasemaps = basemaps.base_maps;
    });
    this.basemapId = loadBasemapPreference(this.auth.getCurrentUser()?.id, data.projectId) || 'default';
    // W75: a record linked to a sheet shows the crop around its pin in the strip's tile: its revision's page render
    const linked = this.existingLink?.sheet;
    if (canReadDrawings && linked?.revision_uuid && !this.existingLink.map_feature_ref) {
      void this.revisionFor(linked.revision_uuid).then(() => { if (!this.destroyed) this.cdr.markForCheck(); });
      // Audit 3: the saved strokes' box, so the tile's crop shows them
      if (this.existingLink.uuid && linked.has_markup) void this.loadSavedStrokes(data.projectId, linked.revision_uuid, this.existingLink.uuid);
    }
  }

  /**
   * Shows the Drawing tab. The picker opens on Map (W49), except for a record linked only to a sheet, or when "Mark on
   * drawing" asked for the Drawing tab; and never once the answer came late and the user already started on the map.
   */
  private enableDrawings(mayOpenOnSheets: boolean, late = true): void {
    this.canUseDrawings = true;
    const sheetOnly = !!this.existingLink?.sheet && !this.existingLink.map_feature_ref;
    if (mayOpenOnSheets && (this.data.initialTab === 'drawing' || sheetOnly)) this.tab = 'drawing';
    // Only a late answer needs a check; in the constructor the view does not exist yet.
    if (late) this.cdr.detectChanges();
  }

  /** The primary action's label: Save (Sri's ruling, matching mobile) */
  readonly saveLabel = PICKER_SAVE_LABEL;
  get recordLabel(): string { return this.data.recordLabel || 'record'; }
  /**
   * The compact modal (Figma "Add station range" 23136:63675 size) for a launcher pick: it never captures (linking
   * closes it), so it does not need the full-screen map a capture is taken from (bd3b7bf2 captures at 2x the map size).
   */
  get compact(): boolean { return !!this.data.featureLauncher; }
  /**
   * The strip's two lines (spec addendum §10). Once the pin has moved off the link's pin the saved offset is stale
   * (Done clears it, see pickResult), so the strip says it is calculated on save.
   */
  get stripLines(): { title: string; detail: string } {
    const link = this.link;
    if (!link) return { title: '', detail: '' };
    const moved = Number(link.pin?.latitude) !== this.latitude || Number(link.pin?.longitude) !== this.longitude;
    return designContextStripLines(moved ? { ...link, offset_label: null } : link);
  }
  get isDoneDisabled(): boolean {
    return (!!this.data.enableLocationSection && !this.locationForm.controls.location.value?.trim())
      || drawingDoneBlocked(this.tab, this.link, this.changes);
  }

  /** W75 (review I3): every change made in this dialog, on either tab: Done on the Drawing tab waits for one */
  get changes(): PickerChanges {
    return { linkTouched: this.linkTouched, pinMoved: this.pinMoved, heldStrokes: !!this.heldSheet,
      locationDirty: this.locationForm.dirty, gpsChanged: this.includeGps !== this.openedWithGps };
  }
  /** The link targets a sheet point (alone, or with a map feature). */
  get isSheetLink(): boolean { return !!this.link?.sheet; }
  /**
   * A sheet-only link: the strip's tile marks up the sheet. A sheet + feature link keeps the map strip (its capture,
   * Edit/Delete markup); its sheet strokes are marked up from the Drawing tab.
   */
  get isSheetStrip(): boolean { return !!this.link?.sheet && !this.link.map_feature_ref; }
  /** The strokes held for the sheet point (shown in the strip's tile until the record saves). */
  /** Strokes held for the sheet link (this session's, or an earlier session's for the record's unsaved link) */
  get heldSheetPreview(): string | null { return (this.heldSheet ?? this.heldForExisting())?.previewDataUrl ?? null; }

  /** W51: the held strokes, while the picked sheet is the one they were drawn on (another sheet shows its own page) */
  get sheetMarkupOverlay(): string | null {
    return heldStrokesFor(this.heldSheet ?? this.heldForExisting(), this.link);
  }

  /** W51: the linked-sheet tile shows the page with its held strokes instead of the pencil */
  /** W75: the linked sheet's page render (the revision the link points at), for the tile's crop around the pin */
  get sheetTileSrc(): string | null {
    const revision = this.sheetRevision;
    return revision && revision.uuid === this.link?.sheet?.revision_uuid ? revision.preview_url || revision.thumb_url || null : null;
  }
  /** The crop of that page around the point, for the square tile; null until the page's aspect is known */
  get sheetTileCrop(): SheetPreviewCrop | null {
    return sheetTileCrop(this.link?.sheet?.point, this.sheetTileSrc, this.sheetTileAspect, this.sheetStrokesBox);
  }
  /**
   * Audit 3: the box the record's strokes cover on the linked page: held in this dialog (or an earlier one, unsaved), else
   * the saved row's, fetched on open
   */
  get sheetStrokesBox(): CropBox | null {
    const held = this.heldSheet ?? this.heldForExisting();
    if (held && held.revisionUuid === this.link?.sheet?.revision_uuid) {
      return markupBBox(held.body?.annotation_data, held.body?.canvas_width, held.body?.canvas_height);
    }
    return this.link?.sheet?.has_markup && this.savedStrokes?.revision === this.link.sheet.revision_uuid ? this.savedStrokes.box : null;
  }

  /** The tile's tooltip and its aria-label alike */
  get sheetTileLabel(): string { return sheetTileLabel(this.hasSheetMarkup); }

  /** Audit 2: the record has strokes on the linked sheet, held in this dialog or saved (Mark up becomes Edit markup) */
  get hasSheetMarkup(): boolean {
    return !!this.heldSheetPreview || !!this.link?.sheet?.has_markup;
  }

  /** The hidden measuring image loaded: the page's aspect clamps the crop */
  onSheetTileLoad(event: Event): void {
    const image = event.target as HTMLImageElement;
    if (image?.naturalWidth && image.naturalHeight) {
      this.sheetTileAspect = { src: image.getAttribute('src') ?? '', aspect: image.naturalWidth / image.naturalHeight };
    }
  }

  get sheetTilePage(): string | null {
    const revision = this.sheetRevision;
    return this.sheetMarkupOverlay && revision?.uuid === this.link?.sheet?.revision_uuid ? revision.preview_url || revision.thumb_url || null : null;
  }
  get capture(): MapFeatureLinkCapture | null { return this.link?.capture ?? null; }
  /** The strip shows the capture (`linkCaptureImage`, the one shared test) or the Mark up tile. */
  get hasCaptureImage(): boolean { return !!linkCaptureImage(this.link); }
  /** Without GPS the result sends no link, so a capture could never attach: Mark up is not offered. */
  get canMarkUp(): boolean {
    if (this.isSheetStrip) return !this.capturing;
    return !!this.link && !!this.mapView && !this.capturing && this.includeGps;
  }
  /** The strip's optional third line: a muted hint (red is only for captureError). */
  get stripHint(): string { return this.capture && this.captureNeedsRetake ? 'Map moved since capture' : ''; }
  /** Against the pin as it is now (the result re-pins the link on Done). */
  get captureNeedsRetake(): boolean {
    return !!this.link && captureNeedsRetake({ ...this.link, pin: { latitude: this.latitude, longitude: this.longitude } });
  }

  async onViewLoaded(view: __esri.MapView): Promise<void> {
    if (this.destroyed) return;
    this.mapView = view;
    this.pinLayer = new GraphicsLayer({ listMode: 'hide' });
    this.highlightLayer = new GraphicsLayer({ listMode: 'hide' });
    view.map.addMany([this.highlightLayer, this.pinLayer]);
    this.drawPin();
    this.applySavedBasemap();
    this.handles = [
      view.on('click', event => { if (!this.dragging) { this.movePin(view.toMap(event)); this.inspectUnlessLinked(); } }),
      view.on('drag', event => { void this.onDrag(event); }),
    ];
    this.applyWhereDrawFilter();
    this.applyLauncherStatus();
    // No published catalog (drawings only): the map is a plain pin, with no design layers to load or to miss.
    if (this.data.hasCatalog === false) {
      this.layersMessage = NO_CATALOG;
      this.cdr.detectChanges();
      return;
    }
    try {
      const accountId = this.auth.getCurrentUser()?.id;
      await this.referenceLayers.activateForProject(view, this.data.projectId, accountId, () => this.auth.getCurrentUser()?.id);
      if (this.destroyed) return;
      if (!this.referenceLayers.isPublishedCatalog()) {
        // The catalog was withdrawn after the launcher's availability answer was cached (manifest 404).
        this.availability.invalidate(this.data.projectId);
        this.layersMessage = LAYERS_UNAVAILABLE;
      } else {
        // The user's own layer visibility on this project (server preferences, shared with the project map).
        this.layersChangedSub = this.referenceLayers.changed.subscribe(() => { this.syncLayerRows(); this.cdr.detectChanges(); });
        this.startSessionLayers();
        if (this.data.featureLauncher && !this.sessionLayers) {
          // The launcher's layer is not in the catalog: say so instead of offering a pick that cannot route.
          this.layersMessage = LAUNCHER_LAYER_UNAVAILABLE;
          this.cdr.detectChanges();
          return;
        }
        await this.frameOnlyLayer(view);
        if (this.destroyed) return;
        this.syncLayerRows();
        await this.showVisibleLayers();
        if (this.destroyed) return;
        if (!this.link) await this.inspectAtPin();
      }
    } catch (error) {
      if (this.destroyed) return;
      if (error?.status === 410) this.availability.invalidate(this.data.projectId);
      this.layersMessage = LAYERS_UNAVAILABLE;
    }
    this.cdr.detectChanges();
  }

  /**
   * Launcher (Addendum 3): its layer is always drawn from the features endpoint so each point can show its status
   * (done / partial / pending); the link summary loads alongside. Without it (offline, error) points keep the layer
   * symbol and rows show no status; picking is unaffected.
   */
  private applyLauncherStatus(): void {
    const launcher = this.data.featureLauncher;
    if (!launcher) return;
    this.referenceLayers.setDrawFilter(launcher.layer, {
      matches: () => true,
      status: (featureId, properties) => {
        // Every point that expects a test gets a status marker (pending replaces the layer dot) once the summary is
        // in; without it (loading, offline, error) the layer's own symbol: no status is shown rather than a wrong one.
        if (!this.linkSummary) return null;
        return launcherPointStatus(properties, launcher, this.linkSummary[featureId]);
      },
    });
    void this.loadLinkSummary();
  }

  /** Fetches the launcher layer's link summary; a failure leaves statuses off. */
  async loadLinkSummary(): Promise<void> {
    const launcher = this.data.featureLauncher;
    if (!launcher || (typeof navigator !== 'undefined' && navigator.onLine === false)) return;
    try {
      const summary = await this.manifestApi.featureLinkSummary(this.data.projectId, launcher.layer);
      if (this.destroyed) return;
      this.linkSummary = summary.features;
      this.referenceLayers.restyleDrawing?.(launcher.layer);
      this.cdr.detectChanges();
    } catch {
      // No statuses; the pick itself does not depend on them.
    }
  }

  /**
   * A launcher row's newest linked entry of its target form holding the row's values (done), or null (pending / no
   * summary). With one form for every test, "Density · 05" needs an entry whose test_type is "Density Test".
   */
  rowEntry(candidate: MapFeatureCandidate | null): FeatureLinkEntry | null {
    if (!this.linkSummary || !candidate) return null;
    return launcherRowEntry(this.launcherRoute(candidate), candidate.properties, this.data.featureLauncher,
      this.linkSummary[candidate.featureId]);
  }

  /** "<Form name> · <Mon D>, <name>" for a done row. */
  rowDoneCaption(candidate: MapFeatureCandidate | null): string {
    return doneCaption(this.rowEntry(candidate));
  }

  /** Whether "Open existing entry" can open the selected row's entry (it is in the report the launcher came from). */
  get canOpenExisting(): boolean {
    return entryInReport(this.rowEntry(this.selected), this.data.featureLauncher?.currentReport);
  }

  /** Shown instead of opening an entry saved on another report. */
  get existingEntryNote(): string {
    const date = shortReportDate(this.rowEntry(this.selected)?.reportDate);
    return date ? `Saved on ${date} report` : 'Saved on another report';
  }

  /** "Open existing entry": close with the entry; the host opens it in the editor. */
  openExistingEntry(): void {
    const entry = this.rowEntry(this.selected);
    if (!entry || !this.canOpenExisting || this.capturing) return;
    this.discardOrphans(null);
    this.dialogRef.close({ open_entry: entry });
  }

  /** True when the form's feature_source filters its layer's features (`where`). */
  get whereActive(): boolean {
    const source = this.data.featureSource;
    return !!source?.layer && !!source.where && typeof source.where === 'object' && Object.keys(source.where).length > 0;
  }

  /** The where-filter empty state, named after the layer. */
  get whereEmptyMessage(): string {
    const layerId = this.data.featureSource?.layer;
    const name = this.referenceLayers.getActiveLayers().find(entry => entry.summary.id === layerId)?.summary.name || 'layer';
    return `No ${name} features match this form here`;
  }

  /**
   * feature_source `where`: this picker's map draws only that layer's matching features (never its unfiltered
   * snapshot; nothing when they cannot be fetched). This instance of the layer service belongs to the dialog,
   * so the project map is unaffected.
   */
  private applyWhereDrawFilter(): void {
    if (!this.whereActive) return;
    const { layer, where } = this.data.featureSource;
    this.referenceLayers.setDrawFilter(layer, {
      matches: properties => featureMatchesWhere(where, properties, this.data.featureSource.multi_value_fields),
      onDrawn: matching => {
        if (this.destroyed) return;
        this.noMatchingFeatures = matching === 0;
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * feature_source `picker: "only"`: show only that layer, as session state. Skipped when the catalog does not
   * have the layer, so a stale config never hides every layer.
   */
  private startSessionLayers(): void {
    if (!this.onlyLayer) return;
    const entries = this.referenceLayers.getActiveLayers().filter(entry => entry.published);
    if (!entries.some(entry => entry.published.id === this.onlyLayer)) return;
    this.sessionLayers = Object.fromEntries(entries.map(entry => [entry.summary.id, { visible: entry.published.id === this.onlyLayer }]));
  }

  /**
   * One-layer pickers open on their layer (mobile 13933f6bd): a launcher frames the layer, or the user standing at it,
   * and moves the pin there (it has no record pin to keep). A picker:"only" form picker without a link whose pin is
   * off the layer frames the layer too, but keeps the pin (it is the record's location).
   */
  private async frameOnlyLayer(view: __esri.MapView): Promise<void> {
    if (!this.sessionLayers || !this.onlyLayer) return;
    const published = this.referenceLayers.getActiveLayers().find(entry => entry.published?.id === this.onlyLayer)?.published;
    if (!published) return;
    const launcher = !!this.data.featureLauncher;
    if (!launcher && (this.existingLink || withinBounds(published.bounds, this.latitude, this.longitude, FRAME_USER_RADIUS_M))) return;
    // The view emits before it is ready: goTo then throws (reading 'animation') and its size is still 0.
    let timer: ReturnType<typeof setTimeout>;
    const ready = await Promise.race([
      Promise.resolve(view.when()).then(() => true, () => false),
      new Promise<boolean>(resolve => { timer = setTimeout(() => resolve(false), VIEW_READY_TIMEOUT_MS); }),
    ]);
    clearTimeout(timer);
    if (!ready || this.destroyed) return;
    const frame = pickerFrame({ bounds: published.bounds, view: { width: view.width, height: view.height },
      user: launcher ? this.userLocation() : null, layerMinZoom: published.minZoom });
    if (!frame || !(await this.moveViewTo(view, frame.center, frame.zoom)) || this.destroyed || !launcher) return;
    // The pin, and with it the first lookup, follows the framed view.
    this.movePin(new Point({ longitude: frame.center[0], latitude: frame.center[1] }));
  }

  /** goTo once ready; if it still fails, set the camera directly. False when neither worked. */
  private async moveViewTo(view: __esri.MapView, center: [number, number], zoom: number): Promise<boolean> {
    try {
      await view.goTo({ center, zoom }, { animate: false });
      return true;
    } catch {
      if (this.destroyed) return false;
    }
    try {
      view.center = new Point({ longitude: center[0], latitude: center[1] });
      view.zoom = zoom;
      return true;
    } catch {
      return false;
    }
  }

  /** The device location the app last stored, if any. */
  private userLocation(): { latitude: number | string | null; longitude: number | string | null } | null {
    let location: { latitude?: number | string | null; longitude?: number | string | null } | null = null;
    try {
      this.store.select(currentLocation).pipe(take(1)).subscribe(value => { location = value; });
    } catch {
      return null;
    }
    return location ? { latitude: location.latitude ?? null, longitude: location.longitude ?? null } : null;
  }

  /** The user's saved layer state, with this session's feature_source overrides on top. */
  private layerRowState(published: PublishedMapLayer): { visible: boolean; opacity: number; labels: boolean } {
    return { ...this.referenceLayers.layerState(published), ...(this.sessionLayers?.[published.id] || {}) };
  }

  /** Rows for the layers popup (published layers only: the picker opens only on a published catalog). */
  private syncLayerRows(): void {
    const entries = this.referenceLayers.getActiveLayers().filter(entry => entry.published);
    const visible = entries.filter(entry => this.layerRowState(entry.published).visible).map(entry => entry.summary.id);
    this.layersToolbar = {
      ...this.layersToolbar,
      visibleOverlayIds: visible,
      overlayLayers: entries.map(entry => ({
        id: entry.summary.id, name: entry.summary.name, opacity: entry.layer.opacity,
        group: this.referenceLayers.publishedGroupName(entry.published),
        labels: entry.published.assets.some(asset => asset.role === 'labels') ? this.layerRowState(entry.published).labels : undefined,
        extent: entry.summary.extent, status: entry.status, error: entry.error,
      })),
    };
  }

  /** Adds and loads the layers this user shows; hides the rest. Keeps the highlight and pin above them. */
  private async showVisibleLayers(): Promise<void> {
    const view = this.mapView;
    if (!view) return;
    const visible = new Set(this.layersToolbar.visibleOverlayIds);
    await this.referenceLayers.showOnMap(view, entry => visible.has(entry.summary.id));
    if (this.destroyed || !this.highlightLayer || !this.pinLayer) return;
    view.map.reorder(this.highlightLayer, view.map.layers.length - 1);
    view.map.reorder(this.pinLayer, view.map.layers.length - 1);
  }

  private saveLayerState(layerId: string, patch: Partial<{ visible: boolean; opacity: number; labels: boolean }>): void {
    this.referenceLayers.savePublishedViewState(layerId, patch).catch(() => {
      if (this.destroyed) return;
      this.layersToolbar = { ...this.layersToolbar, overlayError: 'Your layer preference could not be saved.' };
      this.syncLayerRows();
      void this.showVisibleLayers();
      this.cdr.detectChanges();
    });
  }

  /** A layer shown or hidden here is the same preference the project map reads. The pick follows what is visible. */
  async onLayerToggle(layerId: string): Promise<void> {
    const entry = this.referenceLayers.getActiveLayers().find(item => item.summary.id === layerId);
    if (!entry?.published) return;
    const visible = !this.layerRowState(entry.published).visible;
    if (this.sessionLayers) this.sessionLayers[layerId] = { ...this.sessionLayers[layerId], visible };
    else this.saveLayerState(layerId, { visible });
    this.syncLayerRows();
    await this.showVisibleLayers();
    if (this.destroyed) return;
    this.inspectUnlessLinked();
  }

  onLayerOpacity(change: { id: string; opacity: number }): void {
    if (!Number.isFinite(change.opacity) || change.opacity < 0 || change.opacity > 1) return;
    this.referenceLayers.setOpacity(change.id, change.opacity);
    if (!this.sessionLayers) this.saveLayerState(change.id, { opacity: change.opacity });
    this.syncLayerRows();
  }

  onLayerLabels(layerId: string): void {
    const entry = this.referenceLayers.getActiveLayers().find(item => item.summary.id === layerId);
    if (!entry?.published?.assets.some(asset => asset.role === 'labels')) return;
    const labels = !this.layerRowState(entry.published).labels;
    this.referenceLayers.setLabels(layerId, labels);
    if (this.sessionLayers) this.sessionLayers[layerId] = { ...this.sessionLayers[layerId], labels };
    else this.saveLayerState(layerId, { labels });
    this.syncLayerRows();
  }

  async onLayersReset(): Promise<void> {
    if (this.sessionLayers) {
      // Back to the feature_source view (that layer only), still without saving anything.
      this.referenceLayers.getActiveLayers().forEach(entry => {
        if (!entry.published) return;
        const saved = this.referenceLayers.layerState(entry.published);
        this.referenceLayers.setOpacity(entry.summary.id, saved.opacity);
        this.referenceLayers.setLabels(entry.summary.id, saved.labels);
      });
      this.startSessionLayers();
      this.syncLayerRows();
      await this.showVisibleLayers();
      if (!this.destroyed) this.inspectUnlessLinked();
      return;
    }
    this.referenceLayers.resetPublishedViewState().catch(() => {
      if (this.destroyed) return;
      this.layersToolbar = { ...this.layersToolbar, overlayError: 'Your published layer preferences could not be reset.' };
      this.cdr.detectChanges();
    });
    this.syncLayerRows();
    await this.showVisibleLayers();
    if (!this.destroyed) this.inspectUnlessLinked();
  }

  /** Fits the layer's stored extent, clamped to its published zoom range. The pin stays where it is. */
  zoomToLayer(layerId: string): void {
    const view = this.mapView;
    const entry = this.referenceLayers.getActiveLayers().find(item => item.summary.id === layerId);
    const saved = entry?.summary.extent;
    if (!view || !saved || ![saved.xmin, saved.ymin, saved.xmax, saved.ymax].every(Number.isFinite)) return;
    const target = new Extent({ xmin: saved.xmin, ymin: saved.ymin, xmax: saved.xmax, ymax: saved.ymax, spatialReference: { wkid: 4326 } });
    view.goTo(target).then(() => {
      const zoom = clampZoomToPublishedMax(view.zoom, entry.published);
      return zoom === null ? undefined : view.goTo({ center: target.center, zoom });
    }).catch(() => undefined);
  }

  zoomBy(delta: number): void {
    const view = this.mapView;
    if (view) view.goTo({ zoom: view.zoom + delta }, { duration: 300 }).catch(() => undefined);
  }

  /** The basemap shown is the one captured; the choice is saved for the project map too. */
  onBasemapChanged(basemap: { id?: string }): void {
    if (!basemap?.id) return;
    this.esriMap?.selectBasemap(basemap);
    this.basemapId = basemap.id;
    saveBasemapPreference(this.auth.getCurrentUser()?.id, this.data.projectId, basemap.id);
  }

  private applySavedBasemap(): void {
    const basemap = resolveBasemap(this.basemapId, this.organizationBasemaps);
    if (!basemap || basemap.id === 'default') { this.basemapId = 'default'; return; }
    this.esriMap?.selectBasemap(basemap);
  }

  private async onDrag(event: __esri.ViewDragEvent): Promise<void> {
    const view = this.mapView;
    if (!view || !this.pinGraphic) return;
    if (event.action === 'start') {
      // Decide synchronously (an awaited hitTest is too late to stop the map panning).
      if (this.pinHit(view, event.x, event.y)) { this.dragging = true; event.stopPropagation(); }
      return;
    }
    if (!this.dragging) return;
    event.stopPropagation();
    this.movePin(view.toMap(event));
    if (event.action === 'end') {
      this.dragging = false;
      this.inspectUnlessLinked();
    }
  }

  /** The pin image sits above its tip (pinSymbol: 20×27, tip at the point), plus a few px of slop. */
  private pinHit(view: __esri.MapView, x: number, y: number): boolean {
    const tip = view.toScreen(new Point({ latitude: this.latitude, longitude: this.longitude }));
    if (!tip) return false;
    return Math.abs(x - tip.x) <= 14 && y <= tip.y + 4 && y >= tip.y - 31;
  }

  private movePin(point: __esri.Point | null): void {
    if (!point || !Number.isFinite(point.latitude) || !Number.isFinite(point.longitude)) return;
    this.latitude = point.latitude;
    this.longitude = point.longitude;
    this.pinMoved = true;
    this.drawPin();
  }

  private drawPin(): void {
    if (!this.pinLayer) return;
    this.pinLayer.removeAll();
    this.pinGraphic = new Graphic({ geometry: new Point({ latitude: this.latitude, longitude: this.longitude }), symbol: pinSymbol() });
    this.pinLayer.add(this.pinGraphic);
  }

  /** While a link is shown the candidate list is hidden, so a moved pin only re-pins (like the initial load). */
  private inspectUnlessLinked(): void {
    if (!this.link) void this.inspectAtPin();
    else this.cdr.detectChanges();
  }

  /** Same query the sites map runs on click, centred on the pin. Keeps the current link until the user changes it. */
  async inspectAtPin(): Promise<void> {
    const view = this.mapView;
    const scope = this.referenceLayers.getPublishedInspectionScope();
    this.resetInspection();
    if (!view || !scope || !view.spatialReference?.isWebMercator) { this.cdr.detectChanges(); return; }
    const generation = this.generation;
    const controller = new AbortController();
    this.controller = controller;
    const order = new Map(view.map.layers.toArray().map((layer, index) => [layer, index]));
    const entries = this.referenceLayers.getActiveLayers().filter(entry => entry.published && entry.status === 'ready'
      && entry.layer.visible && entry.layer.opacity > 0 && order.has(entry.layer)
      // Clickable wherever it is drawn, not the published zoom range
      && layerDrawsAtScale(entry.layer, view.scale)
      // feature_source `picker: "only"`: features of that layer alone, even if the user turns others on.
      && (!this.sessionLayers || entry.published.id === this.onlyLayer));
    const screen = view.toScreen(new Point({ latitude: this.latitude, longitude: this.longitude }));
    const query = screen && buildPickQuery({ projectId: scope.projectId, catalogRevision: scope.catalogRevision, screen,
      toMap: (x, y) => { const p = view.toMap({ x, y }); return p ? [p.longitude, p.latitude] : null; },
      resolution: view.resolution, layers: entries.map(entry => ({ published: entry.published, drawOrder: order.get(entry.layer) })) });
    if (!query) { this.message = 'No design features here. Zoom in or move the pin.'; this.cdr.detectChanges(); return; }
    if (!query.layers.some(l => l.layer.inspect === 'full')) { this.message = 'This reference layer supports viewing only.'; this.cdr.detectChanges(); return; }
    this.query = query;
    this.loading = true;
    this.cdr.detectChanges();
    try {
      const result = await this.inspection.inspect(query, controller.signal);
      if (generation !== this.generation) return;
      this.incomplete = result.incomplete;
      const candidates = this.offeredCandidates(result.candidates);
      this.hidden = Math.max(0, candidates.length - MAX_PICK_CHOICES);
      this.candidates = candidates.slice(0, MAX_PICK_CHOICES);
      const metresPerPixel = view.resolution * Math.cos(query.point[1] * Math.PI / 180);
      this.candidates.forEach((candidate, position) => { candidate.label = featureLabel(candidate.fields, position); });
      titleLookAlikes(this.candidates, candidate => candidate.layer.id);
      for (const candidate of this.candidates) {
        candidate.meta = [candidate.layer.name, distanceText(candidate.distance * metresPerPixel)].filter(Boolean).join(' · ');
      }
      if (this.data.featureLauncher) this.labelLauncherCandidates(metresPerPixel);
      if (!this.candidates.length) this.message = this.whereActive ? this.whereEmptyMessage : 'No design features here.';
      this.selected = this.candidates.length === 1 ? this.candidates[0] : null;
      this.view = this.selected ? 'details' : 'list';
      this.highlight(this.selected);
    } catch (error) {
      if (generation !== this.generation) return;
      // A withdrawn catalog (410) must not open ArcGIS again from the stale availability answer.
      if (error?.status === 410) this.availability.invalidate(this.data.projectId);
      this.message = [401, 403, 410].includes(error?.status)
        ? ACCESS_CHANGED
        : 'Feature details could not be loaded. Move the pin to retry.';
    } finally {
      if (generation === this.generation) { this.loading = false; this.cdr.detectChanges(); }
    }
  }

  /** feature_source `where`: features of its layer are offered only when their properties match. */
  private offeredCandidates(candidates: MapFeatureCandidate[]): MapFeatureCandidate[] {
    const launcher = this.data.featureLauncher;
    if (launcher) {
      // Launcher: features of its layer that route to a form of this project, one row per value of a routed
      // multi-value property (each row links the same point; its properties carry that one value).
      return candidates
        .filter(candidate => candidate.layer.id === launcher.layer)
        .flatMap(candidate => expandLauncherProperties(candidate.properties, launcher)
          .map(row => (row.variant ? { ...candidate, properties: row.properties, variant: row.variant } : candidate)))
        .filter(candidate => !!this.launcherRoute(candidate));
    }
    const source = this.data.featureSource;
    if (!source?.layer || !source.where) return candidates;
    return candidates.filter(candidate => candidate.layer.id !== source.layer
      || featureMatchesWhere(source.where, candidate.properties, source.multi_value_fields));
  }

  /** The route a launcher candidate opens (null outside a launcher, or when none matches). */
  launcherRoute(candidate: MapFeatureCandidate | null): ResolvedLauncherRoute | null {
    const launcher = this.data.featureLauncher;
    return launcher && candidate ? matchLauncherRoute(launcher.routes, candidate.properties) : null;
  }

  /** Launcher rows: label_fields joined " · " (else the feature label), then the target form's name and the distance. */
  private labelLauncherCandidates(metresPerPixel: number): void {
    for (const candidate of this.candidates) {
      candidate.label = launcherCandidateLabel(candidate.properties, this.data.featureLauncher.label_fields) || candidate.label;
      candidate.meta = [this.launcherRoute(candidate)?.form?.name, distanceText(candidate.distance * metresPerPixel)].filter(Boolean).join(' · ');
    }
  }

  /** The details action: "Open <form>" in a launcher, else "Link to this <record>". */
  get linkActionLabel(): string {
    const form = this.launcherRoute(this.selected)?.form?.name;
    return this.data.featureLauncher ? `Open ${form || 'form'}` : `Link to this ${this.recordLabel}`;
  }

  private resetInspection(): void {
    this.generation++;
    this.controller?.abort();
    this.controller = undefined;
    this.query = undefined;
    this.loading = false;
    this.message = '';
    this.incomplete = false;
    this.candidates = [];
    this.hidden = 0;
    this.selected = null;
    this.view = 'list';
    this.highlight(null);
  }

  selectCandidate(candidate: MapFeatureCandidate): void {
    this.selected = candidate;
    this.view = 'details';
    this.highlight(candidate);
  }

  backToList(): void {
    this.selected = null;
    this.view = 'list';
    this.highlight(null);
  }

  hoverCandidate(candidate: MapFeatureCandidate | null): void {
    this.highlight(candidate || this.selected);
  }

  private highlight(candidate: MapFeatureCandidate | null): void {
    this.highlightLayer?.removeAll();
    const geometry = candidate && geoJsonToArcGis(candidate.geometry);
    if (!geometry || !this.highlightLayer) return;
    this.highlightLayer.add(new Graphic({ geometry: geometry as unknown as __esri.GeometryUnion,
      symbol: featureHighlightSymbol(geometry.type) as unknown as __esri.SymbolUnion }));
  }

  /** "Link to this <record>": the draft link the editors save with the record. */
  linkSelected(): void {
    if (!this.query || !this.selected) return;
    this.link = draftMapFeatureLink(this.query, this.selected, { latitude: this.latitude, longitude: this.longitude });
    this.linkedProperties = this.data.featureSource || this.data.featureLauncher ? { ...(this.selected.properties || {}) } : null;
    this.linkTouched = true;
    this.highlight(null);
    // Launcher: the link is the choice; the host opens the routed form right away.
    if (this.data.featureLauncher) { this.done(); return; }
    this.cdr.detectChanges();
  }

  /** Change needs the design layers; without them it would silently turn into Remove. */
  /**
   * The candidate list was frozen while the link was shown (a moved pin only re-pins), so it may belong
   * to an earlier pin position: inspect again at the current pin rather than bring back stale rows.
   */
  /** W77: the ••• Remove link: asks first only when markup would go with it (held strokes, saved record markup, a marked-up capture) */
  removeLink(): void {
    if (!this.link) return;
    confirmLinkRemoval(this.dialog, this.link, !!(this.heldSheet ?? this.heldForExisting())).subscribe(go => {
      if (go && !this.destroyed) {
        this.dropLink();
        this.cdr.detectChanges();
      }
    });
  }

  private dropLink(): void {
    this.link = null;
    this.heldSheet = null;
    this.linkedProperties = null;
    this.linkTouched = true;
    void this.inspectAtPin();
  }

  /**
   * Mark up (spec 2026-09-27 §2.2-2.3): wait for the map to settle, capture what is drawn, upload it
   * with the pin as its location, put it on the link and open the markup editor on it.
   * A failure leaves the link exactly as it was and says why.
   */
  async markUp(): Promise<void> {
    const view = this.mapView;
    if (!this.link || !view || this.capturing || !this.includeGps) return;
    const previous = this.link.capture?.attachment_uuid ?? null;
    this.capturing = true;
    this.captureError = '';
    this.cdr.detectChanges();
    let shot: MapViewCapture;
    try {
      shot = await this.captureMap(view);
    } catch {
      this.failCapture(CAPTURE_FAILED);
      return;
    }
    let capture: MapFeatureLinkCapture;
    try {
      // Any throw here (a rejected request, or atob on a malformed data URL) is an upload failure.
      capture = await this.captureService.upload(shot.dataUrl, { latitude: this.latitude, longitude: this.longitude });
    } catch {
      this.failCapture(CAPTURE_UPLOAD_FAILED);
      return;
    }
    if (this.destroyed || !this.link) {
      // The dialog or the link went away mid-upload: nothing will attach this upload.
      this.capturing = false;
      void this.captureService.delete(capture.attachment_uuid).catch(() => undefined);
      if (!this.destroyed) this.cdr.detectChanges();
      return;
    }
    this.session.track(capture.attachment_uuid);
    // The region rides on the capture; the payload sends it as map_feature_link.capture_region (spec addendum §11).
    this.link = withCapture(this.link, { ...capture, region: shot.region });
    this.linkTouched = true;
    this.capturing = false;
    const orphan = this.session.replaceable(previous);
    if (orphan) {
      this.session.forget(orphan);
      void this.captureService.delete(orphan).catch(() => undefined);
    }
    this.cdr.detectChanges();
    this.editMarkup();
  }

  private failCapture(message: string): void {
    this.capturing = false;
    if (this.destroyed) return;
    this.captureError = message;
    this.cdr.detectChanges();
  }

  /** The strip's image (Sri 2026-09-28: no inline buttons): Edit markup on a capture, otherwise capture and Mark up. */
  tapImage(): Promise<void> | void {
    if (this.isSheetStrip) return this.markUpSheet();
    if (this.hasCaptureImage) return this.editMarkup();
    if (this.canMarkUp) return this.markUp();
  }

  /**
   * "Delete markup" in the strip's "…" menu (Sri 2026-09-28; there is no Retake): the link drops its capture, so the
   * Mark up slot comes back. A capture this dialog uploaded is deleted now; the record's own goes when the api saves
   * the link without it.
   */
  deleteMarkup(): void {
    const uuid = this.link?.capture?.attachment_uuid ?? null;
    if (!this.link?.capture || this.capturing) return;
    this.link = withCapture(this.link, null);
    this.linkTouched = true;
    this.captureError = '';
    const orphan = this.session.replaceable(uuid);
    if (orphan) {
      this.session.forget(orphan);
      void this.captureService.delete(orphan).catch(() => undefined);
    }
    this.cdr.detectChanges();
  }

  /** "Delete markup" in the "…": it destroys the user's drawing, so it asks first (red only in the confirm). */
  confirmDeleteMarkup(): void {
    if (!this.link?.capture || this.capturing) return;
    this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: new ConfirmDialogModel('Delete markup?', 'The map capture and your drawing on it are removed from this link.', 'Delete', 'Cancel', true),
    }).afterClosed().subscribe(confirmed => { if (confirmed) this.deleteMarkup(); });
  }

  /** Overridable seam: the ArcGIS calls (settle + takeScreenshot) live in the helper. */
  protected captureMap(view: __esri.MapView): Promise<MapViewCapture> {
    return captureMapView(view, new Point({ latitude: this.latitude, longitude: this.longitude }));
  }

  /** The existing editor, inline (it renders itself as a full-screen overlay), on the clean original. */
  editMarkup(): void {
    const capture = this.link?.capture;
    if (!capture || this.showMarkupEditor) return;
    const imageUrl = capture.original_url || capture.preview_url || capture.thumb_url;
    const open = (existingMarkup: Markup | null) => {
      if (this.destroyed || this.link?.capture !== capture) return;
      this.markupConfig = {
        imageUrl, originalUrl: imageUrl, attachmentId: capture.attachment_uuid, existingMarkup, pageNumber: 1,
        filename: `design-capture-${capture.attachment_uuid}.png`,
      };
      this.editorTarget = 'capture';
      this.showMarkupEditor = true;
      this.cdr.detectChanges();
    };
    this.markupService.getByAttachment(capture.attachment_uuid, 1).subscribe({
      next: (markups: Markup[]) => open(markups?.length ? markups[0] : null),
      error: () => open(null),
    });
  }

  /** The Drawing tab placed or moved the point: the link targets that revision (see draftSheetLink). */
  onSheetPicked(pick: SheetPointPick): void {
    this.link = draftSheetLink(pick.revision, pick.sheet, pick.point, this.link);
    this.sheetRevision = pick.revision;
    // Strokes belong to a page: another revision's page drops them.
    if (this.heldSheet && this.heldSheet.revisionUuid !== pick.revision.uuid) this.heldSheet = null;
    this.linkTouched = true;
    this.captureError = '';
    this.highlight(null);
    this.cdr.detectChanges();
  }

  private async loadSavedStrokes(projectId: string, revisionUuid: string, linkUuid: string): Promise<void> {
    try {
      const page = await this.sheetPreviews.page(projectId, revisionUuid);
      const layer = page ? await this.sheetPreviews.linkMarkupLayer(page, linkUuid) : null;
      if (!this.destroyed && layer?.box) {
        this.savedStrokes = { revision: revisionUuid, box: layer.box };
        this.cdr.markForCheck();
      }
    } catch { /* the tile keeps today's crop */ }
  }

  /**
   * Audit 2: Remove pin on the sheet. With the record's markup on the sheet (held or saved) the location stays, markup
   * only (no point is invented). Without it the sheet target goes; a feature link keeps its feature. Done sends it.
   */
  removeSheetPin(): void {
    const link = this.link;
    if (!link?.sheet || this.capturing) return;
    if (this.hasSheetMarkup) {
      this.link = sheetPinRemoved(link, true);
      this.linkTouched = true;
      this.sheetPicker?.clearPoint();
      this.cdr.detectChanges();
      return;
    }
    this.dropSheetTarget();
  }

  /** The sheet target goes (no pin and no markup is no drawing location); a feature link keeps its feature. */
  private dropSheetTarget(): void {
    this.link = sheetPinRemoved(this.link);
    this.heldSheet = null;
    this.linkTouched = true;
    this.sheetPicker?.clearPoint();
    this.cdr.detectChanges();
  }

  private async revisionFor(revisionUuid: string): Promise<DrawingRevisionDetail | null> {
    if (this.sheetRevision?.uuid === revisionUuid) return this.sheetRevision;
    try {
      this.sheetRevision = await firstValueFrom(this.drawings.getRevision(this.data.projectId, revisionUuid));
    } catch {
      return null;
    }
    return this.sheetRevision;
  }

  /**
   * Mark up on the sheet (R16): one shared row tagged with the record's link. A saved link (same revision) saves
   * straight to its row; a link that does not exist yet keeps the strokes until the record saves (SheetMarkupQueueService).
   */
  async markUpSheet(target?: SheetMarkUpTarget): Promise<void> {
    const link = this.link;
    const sheet = link?.sheet;
    // The Drawing tab's Mark up on a sheet the link is not on (or with no link yet): markup with no pin
    const pending = target && target.revision.uuid !== sheet?.revision_uuid ? target : null;
    if ((!sheet && !pending) || this.showMarkupEditor || this.capturing) return;
    const revision = pending ? pending.revision : await this.revisionFor(sheet.revision_uuid);
    if (this.destroyed || this.link !== link) return;
    if (!revision?.file) {
      this.captureError = SHEET_UNAVAILABLE;
      this.cdr.detectChanges();
      return;
    }
    const held = pending || link.uuid ? null : this.heldSheet ?? this.heldForExisting();
    this.pendingSheet = pending;
    this.markupConfig = {
      imageUrl: revision.preview_url, originalUrl: revision.file.original_url, attachmentId: revision.file.attachment_uuid,
      pageNumber: revision.page_number, isDocument: true, visibility: 'shared',
      filename: `${revision.sheet?.number ?? 'Sheet'} Rev ${revision.label ?? ''}`.trim(),
      ...(link?.uuid && !pending ? { linkUuid: link.uuid } : { deferSave: true, existingMarkup: held ? heldMarkup(held) : undefined }),
      recordKind: this.data.markupKind ?? null,
      // W51: the record's point, as a pin the user draws around (not part of the markup)
      contextPin: !pending && sheet.point ? { x: Number(sheet.point.x), y: Number(sheet.point.y) } : null,
      // Rule 5: with no pin, clearing the markup removes the location; the editor asks first
      confirmEmptySave: !pending && !sheet.point ? PINLESS_EMPTY_CONFIRM : null,
    };
    this.editorTarget = 'sheet';
    this.captureError = '';
    this.showMarkupEditor = true;
    this.cdr.detectChanges();
  }

  /** Strokes an earlier session held for the record's unsaved sheet link (the picker re-opened before the save). */
  private heldForExisting(): HeldSheetMarkup | null {
    return this.existingLink?.sheet ? this.sheetMarkups.heldFor(this.existingLink) : null;
  }

  onEditorSaved(result: MarkupSaveResult): void {
    if (this.editorTarget === 'sheet') this.onSheetMarkupSaved(result);
    else this.onCaptureMarkupSaved(result);
  }

  private onSheetMarkupSaved(result: MarkupSaveResult): void {
    const config = this.markupConfig;
    const pending = this.pendingSheet;
    this.pendingSheet = null;
    const hasStrokes = !!result.markup?.annotation_data?.objects?.length;
    if (pending) {
      // Markup with no pin: the record's location is the sheet, its markup shows where (nothing drawn, nothing linked)
      if (!hasStrokes) { this.closeMarkupEditor(); return; }
      this.link = draftSheetLink(pending.revision, pending.sheet, null, this.link);
      this.sheetRevision = pending.revision;
      this.linkTouched = true;
      this.highlight(null);
    }
    const sheet = this.link?.sheet;
    if (sheet && !sheet.point && !hasStrokes) {
      // Rule 5 (the editor asked): no pin and no markup is no drawing location
      this.closeMarkupEditor();
      this.dropSheetTarget();
      return;
    }
    if (sheet && config?.deferSave) {
      const markup = result.markup;
      this.heldSheet = {
        // One key per record link: an unsaved link keeps its key across re-opens, a saved one removed and re-picked here
        // keeps its stored key, and only a link with none gets a new one (the api stores the key sent and echoes it)
        key: this.heldForExisting()?.key ?? this.existingLink?.client_key ?? newClientKey(),
        revisionUuid: sheet.revision_uuid, point: sheet.point, attachmentUuid: config.attachmentId, pageNumber: config.pageNumber,
        sheetNumber: sheet.sheet_number, revisionLabel: sheet.label ?? null, projectId: this.data.projectId ?? null,
        body: { annotation_data: markup.annotation_data, canvas_width: markup.canvas_width, canvas_height: markup.canvas_height },
        previewDataUrl: result.previewDataUrl,
        kind: markup.kind === 'as_built' ? 'as_built' : null,
      };
    } else if (this.link?.uuid) {
      // Saved on the link's row: the api re-renders its crop, so the card follows the pending snapshot. Review M4: both
      // flags follow the strokes now on the row (none left after Delete), so Remove link confirms only when they would go.
      this.link = {
        ...this.link, sheet: sheet ? { ...sheet, has_markup: hasStrokes, ...savedMarkupKind(result.markup, hasStrokes) } : sheet,
        snapshot: { status: 'pending', error: null, attachment_uuid: null, thumb_url: null, preview_url: null, has_markup: hasStrokes },
      };
      this.linkTouched = true;
    }
    this.closeMarkupEditor();
  }

  onCaptureMarkupSaved(result: MarkupSaveResult): void {
    const capture = this.link?.capture;
    if (!capture) { this.closeMarkupEditor(); return; }
    this.markupService.notifyMarkupSaved(capture.attachment_uuid, result.previewDataUrl);
    this.link = withCapture(this.link, captureAfterMarkupSave(capture, result));
    // Review I3: the capture's markup is a change Done waits for
    this.linkTouched = true;
    this.closeMarkupEditor();
  }

  closeMarkupEditor(): void {
    this.showMarkupEditor = false;
    this.pendingSheet = null;
    this.markupConfig = null;
    this.cdr.detectChanges();
  }

  /** Uploads of this dialog that will not attach to the record (spec §3.4 orphans). */
  private discardOrphans(kept: string | null): void {
    for (const uuid of this.session.orphans(kept)) {
      this.session.forget(uuid);
      void this.captureService.delete(uuid).catch(() => undefined);
    }
  }

  buildResult(): PickResult {
    return pickResult({ includeGps: this.includeGps, latitude: this.latitude, longitude: this.longitude,
      enableLocationSection: !!this.data.enableLocationSection, locationText: this.locationForm.controls.location.value || null,
      link: this.link, existingLink: this.existingLink, linkTouched: this.linkTouched, pinMoved: this.pinMoved,
      featureProperties: this.linkedProperties });
  }

  /**
   * Done: strokes on an unsaved sheet link go to the queue under that link's `client_key`, which the result's link
   * carries to the record's save, so the save success posts them on the link this record created (review #1).
   * Strokes held for the record's earlier point follow a move on the same revision, under the same key.
   */
  private handOverSheetMarkup(next: MapFeatureLink | null | undefined): MapFeatureLink | null | undefined {
    if (next === undefined) return next;
    const strokes = this.heldSheet ?? this.heldForExisting();
    if (this.existingLink && !this.existingLink.uuid) this.sheetMarkups.release(this.existingLink.client_key);
    const sheet = next?.sheet;
    if (!strokes || !sheet || next.uuid || strokes.revisionUuid !== sheet.revision_uuid) return next;
    this.sheetMarkups.hold({ ...strokes, point: sheet.point });
    return { ...next, client_key: strokes.key };
  }

  done(): void {
    // An upload still in flight would be stranded by closing: wait for it.
    if (this.isDoneDisabled || this.capturing) return;
    const result = this.buildResult();
    const link = result.map_feature_link;
    if (link && (this.linkTouched || this.heldSheet)) {
      this.track.track('record_location_set', {
        target: link.sheet ? 'sheet' : 'map', has_markup: !!(this.heldSheet || link.capture?.has_markup || link.snapshot?.has_markup),
      });
    }
    // The key stays absent when the result leaves the record's link alone ('map_feature_link' in result means a change)
    const handed = this.handOverSheetMarkup(result.map_feature_link);
    if (handed !== undefined) result.map_feature_link = handed;
    // Only the capture the result carries can attach; everything else uploaded here is an orphan.
    this.discardOrphans(result.map_feature_link?.capture?.attachment_uuid ?? null);
    this.dailyReportService.selectedLocation.next(result);
    this.dialogRef.close(result);
  }

  cancel(): void {
    this.discardOrphans(null);
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.generation++;
    this.controller?.abort();
    this.handles.forEach(handle => handle.remove());
    this.layersChangedSub?.unsubscribe();
    if (this.mapView) {
      this.referenceLayers.deactivate(this.mapView);
      // app-esri-map never destroys its view: without this every open leaks a MapView and a WebGL context.
      this.mapView.destroy();
      this.mapView = null;
    }
  }
}

function hasCoordinates(latitude: unknown, longitude: unknown): boolean {
  return latitude !== null && latitude !== undefined && latitude !== '' && longitude !== null && longitude !== undefined && longitude !== '';
}
